import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  Package, 
  FolderOpen, 
  ShoppingCart, 
  LogOut,
  Plus,
  Edit,
  Trash2,
  Settings as SettingsIcon,
  TrendingUp,
  DollarSign,
  RefreshCcw
} from 'lucide-react';
import { API, BACKEND_URL } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Navbar from '../components/Navbar';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toAbsoluteImageUrl } from '../utils/image';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const username = localStorage.getItem('adminUsername');
    
    if (!token || !username) {
      navigate('/admin/login');
    } else {
      setUser({ username });
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <Navbar />
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
          <p>Hello, {user.username}</p>
        </div>
        
        <nav className="sidebar-nav">
          <Link to="/admin/dashboard" className="nav-item" data-testid="nav-overview">
            <LayoutDashboard size={20} />
            <span>Overview</span>
          </Link>
          <Link to="/admin/categories" className="nav-item" data-testid="nav-categories">
            <FolderOpen size={20} />
            <span>Categories</span>
          </Link>
          <Link to="/admin/products" className="nav-item" data-testid="nav-products">
            <Package size={20} />
            <span>Products</span>
          </Link>
          <Link to="/admin/orders" className="nav-item" data-testid="nav-orders">
            <ShoppingCart size={20} />
            <span>Orders</span>
          </Link>
          <Link to="/admin/analytics" className="nav-item" data-testid="nav-analytics">
            <TrendingUp size={20} />
            <span>Sales Analytics</span>
          </Link>
          <Link to="/admin/settings" className="nav-item" data-testid="nav-settings">
            <SettingsIcon size={20} />
            <span>Settings</span>
          </Link>
        </nav>

        <button onClick={handleLogout} className="logout-btn" data-testid="logout-button">
          <LogOut size={20} />
          <span>Logout</span>
        </button>
      </div>

      <div className="admin-content">
        <Routes>
          <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/dashboard" element={<Overview />} />
          <Route path="/categories" element={<CategoriesManager />} />
          <Route path="/products" element={<ProductsManager />} />
          <Route path="/orders" element={<OrdersManager />} />
          <Route path="/settings" element={<SettingsManager />} />
          <Route path="/analytics" element={<SalesDashboard />} />
        </Routes>
      </div>

      <style jsx>{`
        .admin-dashboard {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background: #F7FAFC;
        }

        .admin-sidebar {
          width: 280px;
          background: white;
          border-right: 1px solid #E2E8F0;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: calc(100vh - 80px);
          left: 0;
          top: 80px;
          z-index: 100;
        }

        .sidebar-header {
          padding: 2rem;
          border-bottom: 1px solid #E2E8F0;
        }

        .sidebar-header h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2D3748;
          font-family: 'Playfair Display', serif;
          margin-bottom: 0.5rem;
        }

        .sidebar-header p {
          color: #718096;
          font-size: 0.9rem;
        }

        .sidebar-nav {
          flex: 1;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          color: #4A5568;
          text-decoration: none;
          border-radius: 10px;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .nav-item:hover {
          background: rgba(255, 155, 155, 0.1);
          color: #FF9B9B;
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          margin: 1rem;
          background: #FED7D7;
          color: #E53E3E;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .logout-btn:hover {
          background: #E53E3E;
          color: white;
        }

        .admin-content {
          margin-left: 280px;
          flex: 1;
          padding: 2rem;
          margin-top: 80px;
        }

        @media (max-width: 968px) {
          .admin-sidebar {
            width: 80px;
          }

          .sidebar-header h2,
          .sidebar-header p,
          .nav-item span,
          .logout-btn span {
            display: none;
          }

          .admin-content {
            margin-left: 80px;
          }
        }
      `}</style>
    </div>
  );
};

const Overview = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalOrders: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [products, categories, orders] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/orders`, { headers })
      ]);

      setStats({
        totalProducts: products.data.length,
        totalCategories: categories.data.length,
        totalOrders: orders.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div data-testid="overview-section">
      <h1 className="page-title">Dashboard Overview</h1>
      
      <div className="stats-grid">
        <div className="stat-card" data-testid="stat-products">
          <Package size={40} />
          <div>
            <p className="stat-value">{stats.totalProducts}</p>
            <p className="stat-label">Total Products</p>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-categories">
          <FolderOpen size={40} />
          <div>
            <p className="stat-value">{stats.totalCategories}</p>
            <p className="stat-label">Categories</p>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-orders">
          <ShoppingCart size={40} />
          <div>
            <p className="stat-value">{stats.totalOrders}</p>
            <p className="stat-label">Total Orders</p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 2rem;
          font-family: 'Playfair Display', serif;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .stat-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .stat-card svg {
          color: #FF9B9B;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
        }

        .stat-label {
          color: #718096;
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
};

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/categories`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category created successfully');
      }
      
      setIsOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', image: '' });
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Error saving category');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.delete(`${API}/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error deleting category');
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      image: category.image || ''
    });
    setIsOpen(true);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem('adminToken');
    const data = new FormData();
    data.append('file', file);

    try {
      setUploadingImage(true);
      const response = await axios.post(`${API}/upload`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      // Set the uploaded image URL
      const imageUrl = response.data.url;
      setFormData((prev) => ({
        ...prev,
        image: imageUrl
      }));
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error uploading image');
    } finally {
      setUploadingImage(false);
      event.target.value = null;
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: ''
    }));
  };

  return (
    <div data-testid="categories-manager">
      <div className="page-header">
        <h1 className="page-title">Manage Categories</h1>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => {
                setEditingCategory(null);
                setFormData({ name: '', description: '', image: '' });
              }}
              data-testid="add-category-button"
            >
              <Plus size={18} />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="category-form" data-testid="category-form">
              <div className="form-group">
                <Label htmlFor="name">Category Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="input-category-name"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="input-category-description"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="image">Category Image</Label>
                <input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  data-testid="input-category-image"
                />
                <p className="help-text">
                  Upload category image from your device. {uploadingImage ? 'Uploading...' : ''}
                </p>
                {formData.image && (
                  <div className="image-preview-single">
                    <img src={formData.image} alt="Category preview" />
                    <button type="button" className="remove-image" onClick={removeImage}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <Button type="submit" className="submit-btn" data-testid="submit-category">
                {editingCategory ? 'Update Category' : 'Create Category'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="data-grid" data-testid="categories-list">
        {categories.map((category) => (
          <div key={category.id} className="data-card" data-testid={`category-${category.id}`}>
            {category.image && (
              <img src={category.image} alt={category.name} className="card-image" />
            )}
            <div className="card-content">
              <h3>{category.name}</h3>
              <p>{category.description}</p>
            </div>
            <div className="card-actions">
              <button onClick={() => openEditDialog(category)} data-testid={`edit-category-${category.id}`}>
                <Edit size={18} />
              </button>
              <button onClick={() => handleDelete(category.id, category.name)} data-testid={`delete-category-${category.id}`}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
          font-family: 'Playfair Display', serif;
        }

        .data-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .data-card {
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .card-image {
          width: 100%;
          height: 150px;
          object-fit: cover;
          border-radius: 10px;
          margin-bottom: 1rem;
        }

        .card-content h3 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.5rem;
        }

        .card-content p {
          color: #718096;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        .card-actions {
          display: flex;
          gap: 0.5rem;
        }

        .card-actions button {
          flex: 1;
          padding: 0.75rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .card-actions button:first-child {
          background: #E6FFFA;
          color: #319795;
        }

        .card-actions button:first-child:hover {
          background: #319795;
          color: white;
        }

        .card-actions button:last-child {
          background: #FED7D7;
          color: #E53E3E;
        }

        .card-actions button:last-child:hover {
          background: #E53E3E;
          color: white;
        }

        .category-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .help-text {
          font-size: 0.9rem;
          color: #718096;
          margin: 0;
        }

        .image-preview-single {
          position: relative;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          overflow: hidden;
          background: #F7FAFC;
          margin-top: 0.75rem;
          max-width: 300px;
        }

        .image-preview-single img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          display: block;
        }

        .remove-image {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(229, 62, 62, 0.9);
          color: white;
          border: none;
          border-radius: 6px;
          padding: 6px 12px;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .remove-image:hover {
          background: #C53030;
        }

        .submit-btn {
          margin-top: 1rem;
        }
      `}</style>
    </div>
  );
};

const ProductsManager = () => {
  const toAbsoluteImageUrl = (image) => {
    if (!image) return '';
    // If it's already a data URL (base64), return as-is
    if (image.startsWith('data:image')) {
      return image;
    }
    const trimmedBackend = BACKEND_URL?.replace(/\/$/, '') || '';
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    const normalizedPath = image.startsWith('/') ? image : `/${image}`;
    return `${trimmedBackend}${normalizedPath}`;
  };

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    inventory: '',
    featured: false,
    images: []
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    if (!formData.images.length) {
      toast.error('Please upload at least one product image');
      return;
    }

    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      inventory: parseInt(formData.inventory),
      images: formData.images
    };
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully');
      }

      setIsOpen(false);
      setEditingProduct(null);
      setFormData({ name: '', description: '', price: '', categoryId: '', inventory: '', featured: false, images: [] });
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error saving product');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete product "${name}"?`)) return;
    
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error deleting product');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      categoryId: product.categoryId,
      inventory: product.inventory.toString(),
      featured: product.featured,
      images: product.images || []
    });
    setIsOpen(true);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem('adminToken');
    const data = new FormData();
    data.append('file', file);

    try {
      setUploadingImage(true);
      const response = await axios.post(`${API}/upload`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      // Add the uploaded image URL to the images array
      const imageUrl = response.data.url;
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, imageUrl]
      }));
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error uploading image');
    } finally {
      setUploadingImage(false);
      event.target.value = null;
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    // Reorder products array
    const newProducts = [...products];
    const draggedProduct = newProducts[draggedIndex];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(dropIndex, 0, draggedProduct);

    // Update local state immediately for smooth UX
    setProducts(newProducts);

    // Send the new order to backend
    const token = localStorage.getItem('adminToken');
    const reorderData = newProducts.map((product, index) => ({
      productId: product.id,
      displayOrder: index
    }));

    try {
      await axios.put(`${API}/products/reorder`, reorderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Products reordered successfully');
    } catch (error) {
      console.error('Error reordering products:', error);
      toast.error('Error reordering products');
      // Revert on error
      fetchProducts();
    }
  };

  return (
    <div data-testid="products-manager">
      <div className="page-header">
        <h1 className="page-title">Manage Products</h1>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingProduct(null);
                  setFormData({ name: '', description: '', price: '', categoryId: '', inventory: '', featured: false, images: [] });
              }}
                data-testid="add-product-button"
              >
              <Plus size={18} />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="product-dialog">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="product-form" data-testid="product-form">
              <div className="form-group">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="input-product-name"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  data-testid="input-product-description"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="price">Price *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                    data-testid="input-product-price"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="inventory">Inventory *</Label>
                  <Input
                    id="inventory"
                    type="number"
                    value={formData.inventory}
                    onChange={(e) => setFormData({...formData, inventory: e.target.value})}
                    required
                    data-testid="input-product-inventory"
                  />
                </div>
              </div>
              <div className="form-group">
                <Label htmlFor="categoryId">Category *</Label>
                <Select value={formData.categoryId} onValueChange={(value) => setFormData({...formData, categoryId: value})}>
                  <SelectTrigger data-testid="select-product-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label htmlFor="images">Product Images *</Label>
                <input
                  id="images"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  data-testid="input-product-images"
                />
                <p className="help-text">
                  Upload product photos from your device. {uploadingImage ? 'Uploading...' : 'You can add multiple images one at a time.'}
                </p>
                {formData.images.length > 0 && (
                  <div className="image-preview-grid">
                    {formData.images.map((image, index) => (
                      <div key={image} className="image-preview-item">
                        <img src={toAbsoluteImageUrl(image)} alt={`Product preview ${index + 1}`} />
                        <button type="button" className="remove-image" onClick={() => removeImage(index)}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                    data-testid="checkbox-product-featured"
                  />
                  <span>Featured Product</span>
                </label>
              </div>
              <Button type="submit" className="submit-btn" data-testid="submit-product">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="products-reorder-notice">
        <p>💡 Drag and drop products to reorder them</p>
      </div>

      <div className="data-grid" data-testid="products-list">
        {products.map((product, index) => {
          const resolvedImage = toAbsoluteImageUrl(product.images?.[0]);

          return (
            <div 
              key={product.id} 
              className="data-card draggable-card" 
              data-testid={`product-${product.id}`}
              draggable="true"
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="drag-handle" title="Drag to reorder">⋮⋮</div>
              <img src={resolvedImage || 'https://via.placeholder.com/400x300?text=No+Image'} alt={product.name} className="card-image" />
              <div className="card-content">
                <h3>{product.name}</h3>
                <p className="product-price">${product.price.toFixed(2)}</p>
                <p className="product-inventory">Stock: {product.inventory}</p>
              </div>
              <div className="card-actions">
                <button onClick={() => openEditDialog(product)} data-testid={`edit-product-${product.id}`}>
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(product.id, product.name)} data-testid={`delete-product-${product.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
          font-family: 'Playfair Display', serif;
        }

        .products-reorder-notice {
          background: linear-gradient(135deg, rgba(255, 155, 155, 0.1) 0%, rgba(180, 167, 255, 0.1) 100%);
          padding: 1rem 1.5rem;
          border-radius: 10px;
          margin-bottom: 1.5rem;
          text-align: center;
          border: 2px dashed #FF9B9B;
        }

        .products-reorder-notice p {
          color: #2D3748;
          font-weight: 600;
          margin: 0;
        }

        .data-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .data-card {
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          position: relative;
        }

        .draggable-card {
          cursor: move;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .draggable-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(0, 0, 0, 0.1);
        }

        .drag-handle {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          font-size: 1.2rem;
          color: #CBD5E0;
          cursor: move;
          user-select: none;
          padding: 0.25rem 0.5rem;
          border-radius: 5px;
          transition: color 0.2s ease, background 0.2s ease;
        }

        .drag-handle:hover {
          color: #FF9B9B;
          background: rgba(255, 155, 155, 0.1);
        }

        .card-image {
          width: 100%;
          height: 200px;
          object-fit: cover;
          border-radius: 10px;
          margin-bottom: 1rem;
        }

        .card-content h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.5rem;
        }

        .product-price {
          font-size: 1.2rem;
          font-weight: 700;
          color: #FF9B9B;
          margin-bottom: 0.25rem;
        }

        .product-inventory {
          color: #718096;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        .card-actions {
          display: flex;
          gap: 0.5rem;
        }

        .card-actions button {
          flex: 1;
          padding: 0.75rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .card-actions button:first-child {
          background: #E6FFFA;
          color: #319795;
        }

        .card-actions button:first-child:hover {
          background: #319795;
          color: white;
        }

        .card-actions button:last-child {
          background: #FED7D7;
          color: #E53E3E;
        }

        .card-actions button:last-child:hover {
          background: #E53E3E;
          color: white;
        }

        .product-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .checkbox-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .checkbox-group input[type="checkbox"] {
          width: 18px;
          height: 18px;
        }

        .help-text {
          font-size: 0.9rem;
          color: #718096;
          margin: 0;
        }

        .image-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 0.75rem;
        }

        .image-preview-item {
          position: relative;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          overflow: hidden;
          background: #F7FAFC;
        }

        .image-preview-item img {
          width: 100%;
          height: 120px;
          object-fit: cover;
          display: block;
        }

        .remove-image {
          position: absolute;
          top: 6px;
          right: 6px;
          background: rgba(229, 62, 62, 0.9);
          color: white;
          border: none;
          border-radius: 6px;
          padding: 4px 8px;
          cursor: pointer;
          font-size: 0.8rem;
        }

        .remove-image:hover {
          background: #C53030;
        }

        .submit-btn {
          margin-top: 1rem;
        }
      `}</style>
    </div>
  );
};

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Error updating order status');
    }
  };

  const handleRefund = async (orderId) => {
    if (!window.confirm('Are you sure you want to refund this order?')) return;
    
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.post(`${API}/orders/${orderId}/refund`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Refund processed successfully');
      fetchOrders();
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error(error.response?.data?.detail || 'Error processing refund');
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to permanently delete order ${orderNumber}? This action cannot be undone.`)) return;
    
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.delete(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted successfully');
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(error.response?.data?.detail || 'Error deleting order');
    }
  };

  return (
    <div data-testid="orders-manager">
      <h1 className="page-title">Manage Orders</h1>

      <div className="orders-list" data-testid="orders-list">
        {orders.map((order) => (
          <div key={order.id} className="order-card" data-testid={`order-${order.id}`}>
            <div className="order-header">
              <div>
                <h3>Order #{order.orderNumber}</h3>
                <p className="order-date">{new Date(order.createdAt).toLocaleString()}</p>
              </div>
              <div className="order-total">${order.total.toFixed(2)}</div>
            </div>

            <div className="order-customer">
              <strong>Customer:</strong> {order.customerInfo.firstName} {order.customerInfo.lastName}
              <br />
              <strong>Email:</strong> {order.customerInfo.email}
              <br />
              <strong>Phone:</strong> {order.customerInfo.phone}
            </div>

            <div className="order-items">
              <strong>Items:</strong>
              {order.items.map((item, idx) => (
                <div key={idx} className="order-item">
                  <span>{item.productName} x {item.quantity}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="order-shipping">
              <strong>Shipping Address:</strong>
              <p>
                {order.shippingAddress.address}, {order.shippingAddress.city}, 
                {order.shippingAddress.state} {order.shippingAddress.zipCode}, 
                {order.shippingAddress.country}
              </p>
            </div>

            <div className="order-status">
              <Label>Status:</Label>
              <Select 
                value={order.status} 
                onValueChange={(value) => updateOrderStatus(order.id, value)}
              >
                <SelectTrigger data-testid={`order-status-${order.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {order.paymentIntentId && !order.refunded && (
              <div className="order-actions">
                <Button 
                  onClick={() => handleRefund(order.id)}
                  className="refund-btn"
                  variant="destructive"
                  data-testid={`refund-order-${order.id}`}
                >
                  <RefreshCcw size={16} />
                  Issue Refund
                </Button>
              </div>
            )}

            {order.refunded && (
              <div className="refund-notice" data-testid={`refund-notice-${order.id}`}>
                ✓ Refunded: ${order.refundAmount.toFixed(2)}
              </div>
            )}

            <div className="order-delete-section">
              <Button 
                onClick={() => handleDeleteOrder(order.id, order.orderNumber)}
                className="delete-order-btn"
                variant="outline"
                data-testid={`delete-order-${order.id}`}
              >
                <Trash2 size={16} />
                Delete Order
              </Button>
            </div>
          </div>
        ))}

        {orders.length === 0 && (
          <div className="no-orders" data-testid="no-orders">
            <p>No orders yet</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 2rem;
          font-family: 'Playfair Display', serif;
        }

        .orders-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .order-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .order-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #F7FAFC;
        }

        .order-header h3 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 0.25rem;
        }

        .order-date {
          color: #718096;
          font-size: 0.9rem;
        }

        .order-total {
          font-size: 1.5rem;
          font-weight: 700;
          color: #FF9B9B;
        }

        .order-customer,
        .order-items,
        .order-shipping {
          margin-bottom: 1.5rem;
          color: #4A5568;
          line-height: 1.7;
        }

        .order-items {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .order-item {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem;
          background: #F7FAFC;
          border-radius: 8px;
          margin-left: 1rem;
        }

        .order-status {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .order-actions {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 2px solid #F7FAFC;
        }

        .refund-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .order-delete-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 2px solid #F7FAFC;
        }

        .delete-order-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          border-color: #E53E3E;
          color: #E53E3E;
        }

        .delete-order-btn:hover {
          background: #FED7D7;
          border-color: #E53E3E;
        }

        .refund-notice {
          margin-top: 1rem;
          padding: 1rem;
          background: #C6F6D5;
          color: #2F855A;
          border-radius: 8px;
          font-weight: 600;
        }

        .no-orders {
          text-align: center;
          padding: 3rem;
          color: #718096;
        }
      `}</style>
    </div>
  );
};

const SettingsManager = () => {
  const [settings, setSettings] = useState({
    cashAppUsername: '',
    venmoUsername: '',
    zelleEmail: '',
    zellePhone: '',
    paymentInstructions: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error saving settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="settings-manager">
      <h1 className="page-title">Store Settings</h1>

      <form onSubmit={handleSubmit} className="settings-form" data-testid="settings-form">
        <div className="settings-section">
          <h2 className="section-title">Cash App Payment</h2>
          <p className="section-description">
            Configure Cash App for customer payments. Customers will see this information at checkout.
          </p>

          <div className="form-group">
            <Label htmlFor="cashAppUsername">Cash App Username (e.g., $YourCashtag)</Label>
            <Input
              id="cashAppUsername"
              value={settings.cashAppUsername || ''}
              onChange={(e) => setSettings({...settings, cashAppUsername: e.target.value})}
              placeholder="$carmenaandino"
              data-testid="input-cashapp-username"
            />
            <p style={{ fontSize: '0.9rem', color: '#718096', marginTop: '0.5rem' }}>
              QR codes will be automatically generated for customers at checkout
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h2 className="section-title">Venmo Payment</h2>
          <p className="section-description">
            Configure Venmo for customer payments. Customers will see this information at checkout.
          </p>

          <div className="form-group">
            <Label htmlFor="venmoUsername">Venmo Username (e.g., @YourVenmo)</Label>
            <Input
              id="venmoUsername"
              value={settings.venmoUsername || ''}
              onChange={(e) => setSettings({...settings, venmoUsername: e.target.value})}
              placeholder="@Carmen-Andino-6"
              data-testid="input-venmo-username"
            />
            <p style={{ fontSize: '0.9rem', color: '#718096', marginTop: '0.5rem' }}>
              QR codes will be automatically generated for customers at checkout
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h2 className="section-title">Zelle Payment</h2>
          <p className="section-description">
            Configure Zelle for customer payments. Provide either email or phone number (or both).
          </p>

          <div className="form-group">
            <Label htmlFor="zelleEmail">Zelle Email Address</Label>
            <Input
              id="zelleEmail"
              type="email"
              value={settings.zelleEmail || ''}
              onChange={(e) => setSettings({...settings, zelleEmail: e.target.value})}
              placeholder="your-email@example.com"
              data-testid="input-zelle-email"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="zellePhone">Zelle Phone Number</Label>
            <Input
              id="zellePhone"
              type="tel"
              value={settings.zellePhone || ''}
              onChange={(e) => setSettings({...settings, zellePhone: e.target.value})}
              placeholder="+1 (555) 123-4567"
              data-testid="input-zelle-phone"
            />
            <p style={{ fontSize: '0.9rem', color: '#718096', marginTop: '0.5rem' }}>
              QR codes will be generated with your Zelle info. Customers will scan and manually enter the amount.
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h2 className="section-title">Payment Instructions</h2>
          <p className="section-description">
            Custom message shown to customers at checkout. Include any special instructions.
          </p>

          <div className="form-group">
            <Label htmlFor="paymentInstructions">Instructions</Label>
            <Textarea
              id="paymentInstructions"
              value={settings.paymentInstructions || ''}
              onChange={(e) => setSettings({...settings, paymentInstructions: e.target.value})}
              placeholder="Please send payment to the above Cash App or Venmo account and include your order number in the note. We'll process your order once payment is received."
              rows={4}
              data-testid="input-payment-instructions"
            />
          </div>
        </div>

        <Button type="submit" disabled={loading} className="save-settings-btn" data-testid="save-settings">
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>

      <style jsx>{`
        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 2rem;
          font-family: 'Playfair Display', serif;
        }

        .settings-form {
          max-width: 800px;
        }

        .settings-section {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          margin-bottom: 2rem;
        }

        .section-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.5rem;
          font-family: 'Playfair Display', serif;
        }

        .section-description {
          color: #718096;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .section-description a {
          color: #FF9B9B;
          text-decoration: none;
        }

        .section-description a:hover {
          text-decoration: underline;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .save-settings-btn {
          width: 100%;
          max-width: 300px;
        }
      `}</style>
    </div>
  );
};

const SalesDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('adminToken');
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error loading analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading" data-testid="loading-analytics">Loading analytics...</div>;
  }

  if (!analytics) {
    return <div className="error" data-testid="error-analytics">Failed to load analytics</div>;
  }

  return (
    <div data-testid="sales-dashboard">
      <div className="dashboard-header">
        <h1 className="page-title">Sales Analytics</h1>
        <Button onClick={fetchAnalytics} data-testid="refresh-analytics">
          <RefreshCcw size={18} />
          Refresh
        </Button>
      </div>

      {/* Revenue Cards */}
      <div className="revenue-grid" data-testid="revenue-cards">
        <div className="revenue-card">
          <div className="card-icon total">
            <DollarSign size={32} />
          </div>
          <div>
            <p className="card-label">Total Revenue</p>
            <p className="card-value" data-testid="total-revenue">${analytics.revenue.total.toFixed(2)}</p>
          </div>
        </div>

        <div className="revenue-card">
          <div className="card-icon today">
            <DollarSign size={32} />
          </div>
          <div>
            <p className="card-label">Today</p>
            <p className="card-value" data-testid="today-revenue">${analytics.revenue.today.toFixed(2)}</p>
          </div>
        </div>

        <div className="revenue-card">
          <div className="card-icon week">
            <DollarSign size={32} />
          </div>
          <div>
            <p className="card-label">This Week</p>
            <p className="card-value" data-testid="week-revenue">${analytics.revenue.week.toFixed(2)}</p>
          </div>
        </div>

        <div className="revenue-card">
          <div className="card-icon month">
            <DollarSign size={32} />
          </div>
          <div>
            <p className="card-label">This Month</p>
            <p className="card-value" data-testid="month-revenue">${analytics.revenue.month.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      {analytics.revenueChart && analytics.revenueChart.length > 0 && (
        <div className="chart-section" data-testid="revenue-chart-section">
          <h2 className="section-title">Revenue Trend (Last 30 Days)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.revenueChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#FF9B9B" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Products */}
      {analytics.topProducts && analytics.topProducts.length > 0 && (
        <div className="chart-section" data-testid="top-products-section">
          <h2 className="section-title">Top Selling Products</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.topProducts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#B4A7FF" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Orders */}
      <div className="recent-orders-section" data-testid="recent-orders-section">
        <h2 className="section-title">Recent Orders</h2>
        <div className="orders-table">
          {analytics.recentOrders.map((order) => (
            <div key={order.id} className="order-row" data-testid={`recent-order-${order.id}`}>
              <div>
                <p className="order-number">{order.orderNumber}</p>
                <p className="order-customer">{order.customerInfo.firstName} {order.customerInfo.lastName}</p>
              </div>
              <div>
                <p className="order-total">${order.total.toFixed(2)}</p>
                <p className={`order-status status-${order.status}`}>{order.status}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
          font-family: 'Playfair Display', serif;
        }

        .revenue-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .revenue-card {
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .card-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .card-icon.total {
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
        }

        .card-icon.today {
          background: rgba(255, 155, 155, 0.2);
          color: #FF9B9B;
        }

        .card-icon.week {
          background: rgba(180, 167, 255, 0.2);
          color: #B4A7FF;
        }

        .card-icon.month {
          background: rgba(167, 255, 212, 0.2);
          color: #38A169;
        }

        .card-label {
          font-size: 0.9rem;
          color: #718096;
          margin-bottom: 0.25rem;
        }

        .card-value {
          font-size: 1.8rem;
          font-weight: 700;
          color: #2D3748;
        }

        .chart-section {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          margin-bottom: 2rem;
        }

        .section-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 1.5rem;
          font-family: 'Playfair Display', serif;
        }

        .recent-orders-section {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .orders-table {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .order-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-radius: 10px;
          background: #F7FAFC;
        }

        .order-number {
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.25rem;
        }

        .order-customer {
          font-size: 0.9rem;
          color: #718096;
        }

        .order-total {
          font-size: 1.1rem;
          font-weight: 700;
          color: #2D3748;
          text-align: right;
          margin-bottom: 0.25rem;
        }

        .order-status {
          font-size: 0.85rem;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-weight: 600;
          text-align: right;
          display: inline-block;
        }

        .status-pending {
          background: #FED7D7;
          color: #E53E3E;
        }

        .status-processing {
          background: #FEEBC8;
          color: #DD6B20;
        }

        .status-shipped {
          background: #BEE3F8;
          color: #2C5282;
        }

        .status-delivered {
          background: #C6F6D5;
          color: #2F855A;
        }

        .loading, .error {
          text-align: center;
          padding: 3rem;
          font-size: 1.1rem;
          color: #718096;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
